import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { TimeEntry } from '../../../shared/models/activity.model';
import { UserService } from '../../../shared/services/user.service';
import { ProjectService } from '../../../shared/services/project.service';

type PeriodMode = 'day' | 'week' | 'month' | 'quarter' | 'all';

interface ProjectSegment {
  name: string;
  hours: number;
  color: string;
  isTask: boolean;
}

interface DayBucket {
  date: Date;
  dateKey: string;        // 'YYYY-MM-DD'
  label: string;          // display label (e.g. 'Lun 14', '14 avr.')
  approvedHours: number;
  draftHours: number;
  totalHours: number;
  entries: TimeEntry[];
  projectSegments: ProjectSegment[];
}

interface ProjectBreakdown {
  name: string;
  hours: number;
  approvedHours: number;
  draftHours: number;
  entries: number;
  type: 'project' | 'task';
  color: string;
}

@Component({
  selector: 'app-my-reporting',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './my-reporting.html',
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ width: '0px', opacity: 0, overflow: 'hidden' }),
        animate('250ms ease-out', style({ width: '340px', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ width: '340px', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ width: '0px', opacity: 0 }))
      ])
    ])
  ],
  styles: [`
    :host {
      display: block;
      width: 100%;
      overflow: hidden;
    }
  `]
})
export class MyReportingComponent implements OnInit, OnDestroy {
  private subs = new Subscription();
  private datePipe = inject(DatePipe);
  private timeEntryService = inject(TimeEntriesService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private elRef = inject(ElementRef);

  // ── State ───────────────────────────────────────────────────────────────
  loading = true;
  /** The connected user's cognitoId – auto-resolved on init */
  private currentEmployeeId = '';
  periodMode: PeriodMode = 'month';
  currentDate = new Date(); // anchor date for navigation

  // Filters
  approvalStatusFilter = '';

  // Project multi-select
  selectedProjects: Set<string> = new Set();
  projectSearch = '';
  projectDropdownOpen = false;
  allKnownProjectNames: Set<string> = new Set();
  projectIdToName: Map<string, string> = new Map();

  // Data
  allEntries: TimeEntry[] = [];
  dayBuckets: DayBucket[] = [];
  selectedDay: DayBucket | null = null;
  projectBreakdowns: ProjectBreakdown[] = [];

  // Summary
  todayHours = 0;
  weekHours = 0;
  monthHours = 0;
  weekTarget = 40;
  monthTarget = 160; // 4 weeks × 40h
  approvedTotal = 0;
  draftTotal = 0;
  totalHours = 0;

  // Chart
  chartMaxHours = 8; // auto-scaled

  // Deterministic color palette for projects
  private readonly PROJECT_COLORS = [
    '#6366f1', // indigo
    '#f59e0b', // amber
    '#10b981', // emerald
    '#ef4444', // red
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#f97316', // orange
    '#ec4899', // pink
    '#14b8a6', // teal
    '#84cc16', // lime
    '#3b82f6', // blue
    '#a855f7', // purple
  ];
  private projectColorCache = new Map<string, string>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.projectDropdownOpen && !this.elRef.nativeElement.querySelector('.project-filter-container')?.contains(event.target)) {
      this.projectDropdownOpen = false;
    }
  }

  async ngOnInit() {
    await Promise.all([
      this.userService.init(),
      this.projectService.listProjects(),
    ]);

    this.subs.add(
      this.projectService.projects$.subscribe(projects => {
        projects.forEach(p => {
          if (p.projectId) this.projectIdToName.set(p.projectId, p.projectName);
        });
      })
    );

    // Resolve connected user's cognitoId — triggers loadData once ready
    this.subs.add(
      this.userService.currentUser$.subscribe(user => {
        if (user?.cognitoId && this.currentEmployeeId !== user.cognitoId) {
          this.currentEmployeeId = user.cognitoId;
          this.loadData();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  // ── Period helpers ──────────────────────────────────────────────────────

  get periodLabel(): string {
    const d = this.currentDate;
    switch (this.periodMode) {
      case 'day': return this.datePipe.transform(d, 'EEEE d MMMM yyyy') ?? '';
      case 'week': {
        const { start, end } = this.getWeekRange(d);
        const s = this.datePipe.transform(start, 'd MMM') ?? '';
        const e = this.datePipe.transform(end, 'd MMM yyyy') ?? '';
        const weekNum = this.getWeekNumber(d);
        return `Week ${weekNum} — ${s} – ${e}`;
      }
      case 'month': return this.datePipe.transform(d, 'MMMM yyyy') ?? '';
      case 'quarter': {
        const q = Math.floor(d.getMonth() / 3) + 1;
        return `Q${q} ${d.getFullYear()}`;
      }
      case 'all': return 'All time';
    }
  }

  get periodRange(): { start: Date; end: Date } {
    const d = this.currentDate;
    switch (this.periodMode) {
      case 'day':
        return {
          start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0),
          end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
        };
      case 'week':
        return this.getWeekRange(d);
      case 'month':
        return {
          start: new Date(d.getFullYear(), d.getMonth(), 1),
          end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
        };
      case 'quarter': {
        const qStart = Math.floor(d.getMonth() / 3) * 3;
        return {
          start: new Date(d.getFullYear(), qStart, 1),
          end: new Date(d.getFullYear(), qStart + 3, 0, 23, 59, 59, 999)
        };
      }
      case 'all':
        return { start: new Date(2020, 0, 1), end: new Date() };
    }
  }

  private getWeekRange(d: Date): { start: Date; end: Date } {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    const start = new Date(d.getFullYear(), d.getMonth(), diff, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  private getWeekNumber(d: Date): number {
    const target = new Date(d.valueOf());
    const dayNr = (d.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
      target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
  }

  /** Whether the 'All' period option should be visible (needs exactly 1 project selected) */
  get canShowAllPeriod(): boolean {
    return this.selectedProjects.size === 1;
  }

  setPeriod(mode: PeriodMode) {
    this.periodMode = mode;
    this.selectedDay = null;
    this.loadData();
  }

  navigatePeriod(direction: -1 | 1) {
    if (this.periodMode === 'all') return;
    const d = new Date(this.currentDate);
    switch (this.periodMode) {
      case 'day': d.setDate(d.getDate() + direction); break;
      case 'week': d.setDate(d.getDate() + direction * 7); break;
      case 'month': d.setMonth(d.getMonth() + direction); break;
      case 'quarter': d.setMonth(d.getMonth() + direction * 3); break;
    }
    this.currentDate = d;
    this.selectedDay = null;
    this.loadData();
  }

  goToToday() {
    this.currentDate = new Date();
    this.selectedDay = null;
    this.loadData();
  }

  // ── Data loading ───────────────────────────────────────────────────────

  async loadData() {
    if (!this.currentEmployeeId) return;

    this.loading = true;
    try {
      // ── 'all' mode: fetch full history for the selected project ──
      if (this.periodMode === 'all' && this.canShowAllPeriod) {
        const key = this.selectedProjects.values().next().value!;
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        let entries: TimeEntry[];

        if (uuidPattern.test(key)) {
          const projectName = this.projectIdToName.get(key);
          console.log('[MyReporting] Fetching ALL entries via GSI for project:', key);
          entries = await this.timeEntryService.fetchAllByProjectId(key, projectName);
        } else {
          console.log('[MyReporting] Fetching ALL entries via scan for project:', key);
          const filters: any = { employeeId: this.currentEmployeeId };
          if (key.startsWith('[Task] ')) {
            filters.crmTaskNames = [key];
          } else {
            filters.baseProjectNames = [key];
          }
          entries = await this.timeEntryService.fetchAllEntries(filters);
        }

        // Scope to current user (GSI doesn't filter by employee)
        entries = entries.filter(e => e.employeeId === this.currentEmployeeId);
        if (this.approvalStatusFilter) {
          entries = entries.filter(e => (e.approvalStatus ?? 'DRAFT') === this.approvalStatusFilter);
        }
        this.allEntries = entries;
      } else {
        // ── Normal date-bounded fetch ──
        const { start, end } = this.periodRange;
        const filters: any = {
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          employeeId: this.currentEmployeeId,
        };
        if (this.approvalStatusFilter) filters.approvalStatus = this.approvalStatusFilter;

        // Project filter
        if (this.selectedProjects.size > 0) {
          const keys = [...this.selectedProjects];
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const projectIds = keys.filter(k => uuidPattern.test(k));
          const crmKeys = keys.filter(k => k.startsWith('[Task] '));
          const nameKeys = keys.filter(k => !uuidPattern.test(k) && !k.startsWith('[Task] '));
          if (projectIds.length) filters.projectIds = projectIds;
          if (nameKeys.length) filters.baseProjectNames = nameKeys;
          if (crmKeys.length) filters.crmTaskNames = crmKeys;
        }

        console.log('[MyReporting] Fetching entries with filters:', filters);
        this.allEntries = await this.timeEntryService.fetchAllEntries(filters);
      }
      console.log('[MyReporting] Fetched', this.allEntries.length, 'entries');

      this.buildKnownProjects();
      this.buildBuckets();
      this.computeSummary();
      this.buildProjectBreakdowns();
    } catch (e) {
      console.error('[MyReporting] Error loading data:', e);
    } finally {
      this.loading = false;
    }
  }

  private buildKnownProjects() {
    this.allEntries.forEach(e => {
      const key = this.getBaseProjectKey(e);
      if (key) {
        this.allKnownProjectNames.add(key);
        if (e.projectId && !e.projectName?.startsWith('[Task] ')) {
          const baseName = (e.projectName ?? '').split(' - ')[0].trim();
          if (baseName) this.projectIdToName.set(e.projectId, baseName);
        }
      }
    });
  }

  private buildBuckets() {
    const { start, end } = this.periodRange;
    const buckets: DayBucket[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (this.periodMode === 'all') {
      // ── All: group by month, derive range from actual data ──
      if (this.allEntries.length === 0) {
        this.dayBuckets = [];
        return;
      }
      let minDate = new Date(this.allEntries[0].startTime);
      let maxDate = new Date(this.allEntries[0].startTime);
      for (const entry of this.allEntries) {
        const d = new Date(entry.startTime);
        if (d < minDate) minDate = d;
        if (d > maxDate) maxDate = d;
      }
      const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const endMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      while (cursor <= endMonth) {
        const dateKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
        const label = `${monthNames[cursor.getMonth()]} ${cursor.getFullYear().toString().slice(-2)}`;
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setMonth(cursor.getMonth() + 1);
      }
      for (const entry of this.allEntries) {
        const d = new Date(entry.startTime);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const bucket = buckets.find(b => b.dateKey === monthKey);
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }
      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    } else if (this.periodMode === 'quarter') {
      // ── Quarter: group by week ──
      const cursor = new Date(start);
      const dayOfWeek = cursor.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      cursor.setDate(cursor.getDate() + diff);

      while (cursor <= end) {
        const weekEnd = new Date(cursor);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const dateKey = this.toDateKey(cursor);
        const label = `${cursor.getDate()}/${cursor.getMonth() + 1}`;
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setDate(cursor.getDate() + 7);
      }

      for (const entry of this.allEntries) {
        const entryDate = new Date(entry.startTime);
        const bucket = buckets.find((b, i) => {
          const nextBucket = buckets[i + 1];
          if (nextBucket) {
            return entryDate >= b.date && entryDate < nextBucket.date;
          }
          return entryDate >= b.date;
        });
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }

      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    } else {
      // ── Day / Week / Month: one bucket per day ──
      const cursor = new Date(start);
      while (cursor <= end) {
        const dateKey = this.toDateKey(cursor);
        const isWeekly = this.periodMode === 'week';
        const isDay = this.periodMode === 'day';
        let label: string;
        if (isDay) {
          label = this.datePipe.transform(cursor, 'HH:mm') ?? '';
        } else if (isWeekly) {
          label = `${dayNames[cursor.getDay()]} ${cursor.getDate()}`;
        } else {
          label = `${cursor.getDate()}`;
        }
        buckets.push({
          date: new Date(cursor),
          dateKey,
          label,
          approvedHours: 0,
          draftHours: 0,
          totalHours: 0,
          entries: [],
          projectSegments: [],
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      for (const entry of this.allEntries) {
        const key = this.toDateKey(new Date(entry.startTime));
        const bucket = buckets.find(b => b.dateKey === key);
        if (bucket) {
          const hours = this.durationHours(entry);
          const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';
          bucket.entries.push(entry);
          bucket.totalHours += hours;
          if (isApproved) bucket.approvedHours += hours;
          else bucket.draftHours += hours;
        }
      }

      for (const bucket of buckets) {
        bucket.projectSegments = this.buildProjectSegments(bucket.entries);
      }
    }

    const max = Math.max(...buckets.map(b => b.totalHours), 0.1);
    this.chartMaxHours = this.niceChartMax(max);

    this.dayBuckets = buckets;
  }

  private computeSummary() {
    const now = new Date();
    const todayKey = this.toDateKey(now);
    const { start: weekStart, end: weekEnd } = this.getWeekRange(now);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

    let todayH = 0, weekH = 0, monthH = 0, approved = 0, draft = 0;

    for (const entry of this.allEntries) {
      const h = this.durationHours(entry);
      const entryDate = new Date(entry.startTime);
      const key = this.toDateKey(entryDate);
      const isApproved = (entry.approvalStatus ?? 'DRAFT') === 'APPROVED';

      if (key === todayKey) todayH += h;
      if (entryDate >= weekStart && entryDate <= weekEnd) weekH += h;
      if (entryDate >= monthStart && entryDate <= monthEnd) monthH += h;
      if (isApproved) approved += h;
      else draft += h;
    }

    this.todayHours = todayH;
    this.weekHours = weekH;
    this.monthHours = monthH;
    this.approvedTotal = approved;
    this.draftTotal = draft;
    this.totalHours = approved + draft;
  }

  private buildProjectBreakdowns() {
    const entries = this.selectedDay ? this.selectedDay.entries : this.allEntries;
    const map = new Map<string, ProjectBreakdown>();

    for (const entry of entries) {
      const name = this.getEntryName(entry.projectName);
      if (!map.has(name)) {
        const isTask = (entry.projectName ?? '').startsWith('[Task] ');
        map.set(name, {
          name,
          hours: 0,
          approvedHours: 0,
          draftHours: 0,
          entries: 0,
          type: isTask ? 'task' : 'project',
          color: this.getProjectColor(name),
        });
      }
      const pb = map.get(name)!;
      const h = this.durationHours(entry);
      pb.hours += h;
      pb.entries++;
      if ((entry.approvalStatus ?? 'DRAFT') === 'APPROVED') pb.approvedHours += h;
      else pb.draftHours += h;
    }

    this.projectBreakdowns = [...map.values()].sort((a, b) => b.hours - a.hours);
  }
  // ── Project color helpers ───────────────────────────────────────────────

  getProjectColor(name: string): string {
    if (this.projectColorCache.has(name)) return this.projectColorCache.get(name)!;
    // Simple deterministic hash
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    const color = this.PROJECT_COLORS[Math.abs(hash) % this.PROJECT_COLORS.length];
    this.projectColorCache.set(name, color);
    return color;
  }

  private buildProjectSegments(entries: TimeEntry[]): ProjectSegment[] {
    const map = new Map<string, ProjectSegment>();
    for (const entry of entries) {
      const name = this.getEntryName(entry.projectName);
      if (!map.has(name)) {
        map.set(name, {
          name,
          hours: 0,
          color: this.getProjectColor(name),
          isTask: (entry.projectName ?? '').startsWith('[Task] '),
        });
      }
      map.get(name)!.hours += this.durationHours(entry);
    }
    // Sort by hours descending so largest segment is at bottom
    return [...map.values()].sort((a, b) => b.hours - a.hours);
  }

  /**
   * Round the tallest bucket up to a clean axis maximum. Steps stay small enough
   * that the tallest column reaches most of the track — the previous rule rounded
   * to 2h above 4h, which could leave the tallest bar at two thirds height.
   */
  private niceChartMax(max: number): number {
    const step = max <= 4 ? 0.5 : max <= 10 ? 1 : 2;
    return Math.max(step, Math.ceil(max / step) * step);
  }

  // ── Chart interactions ─────────────────────────────────────────────────

  selectDay(bucket: DayBucket) {
    if (this.selectedDay?.dateKey === bucket.dateKey) {
      this.selectedDay = null;
    } else {
      this.selectedDay = bucket;
    }
    this.buildProjectBreakdowns();
  }

  clearDaySelection() {
    this.selectedDay = null;
    this.buildProjectBreakdowns();
  }

  /** Bar height as a percentage of the available chart track (fills any container height). */
  getBarPercent(hours: number): number {
    if (this.chartMaxHours === 0 || hours <= 0) return 0;
    return Math.min(100, (hours / this.chartMaxHours) * 100);
  }

  get yAxisLabels(): number[] {
    const steps = 4;
    const labels: number[] = [];
    for (let i = steps; i >= 0; i--) {
      labels.push(Math.round((this.chartMaxHours / steps) * i * 10) / 10);
    }
    return labels;
  }

  /** Minimum pixel width for the chart bars container (enables horizontal scroll for long ranges) */
  get chartMinWidth(): number | null {
    if (this.dayBuckets.length <= 14) return null;
    return this.dayBuckets.length * 48;
  }

  isToday(bucket: DayBucket): boolean {
    return bucket.dateKey === this.toDateKey(new Date());
  }

  /** The dateKey of the bucket that contains "now" (the latest bucket starting on/before today). */
  get currentBucketKey(): string | null {
    const now = Date.now();
    let candidate: DayBucket | null = null;
    for (const b of this.dayBuckets) {
      if (b.date.getTime() <= now) candidate = b;
      else break;
    }
    return candidate?.dateKey ?? null;
  }

  isWeekend(bucket: DayBucket): boolean {
    const day = bucket.date.getDay();
    return day === 0 || day === 6;
  }

  // ── Filter handlers ────────────────────────────────────────────────────

  onFilterChange() {
    this.selectedDay = null;
    this.loadData();
  }

  toggleProject(name: string) {
    const next = new Set(this.selectedProjects);
    if (next.has(name)) next.delete(name); else next.add(name);
    this.selectedProjects = next;
    if (this.periodMode === 'all' && !this.canShowAllPeriod) {
      this.periodMode = 'month';
    }
    this.loadData();
  }

  clearProjectFilter() {
    this.selectedProjects = new Set();
    this.projectSearch = '';
    if (this.periodMode === 'all') {
      this.periodMode = 'month';
    }
    this.loadData();
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
  }

  // ── Utility ────────────────────────────────────────────────────────────

  private toDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  durationHours(entry: TimeEntry): number {
    const ms = new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime();
    return ms / 3600000;
  }

  formatHours(h: number): string {
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
  }

  /** Compact hours label: "132h" when whole, "6h30" otherwise (matches the design). */
  formatHoursShort(h: number): string {
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    return mins === 0 ? `${hours}h` : `${hours}h${String(mins).padStart(2, '0')}`;
  }

  formatPercent(current: number, target: number): string {
    if (target === 0) return '0%';
    return Math.round((current / target) * 100) + '%';
  }

  getBaseProjectKey(entry: { projectName?: string | null; projectId?: string | null }): string {
    const name = entry.projectName ?? '';
    if (name.startsWith('[Task] ')) return name;
    if (entry.projectId) return entry.projectId;
    return name.split(' - ')[0].trim();
  }

  resolveKeyName(key: string): string {
    if (this.projectIdToName.has(key)) return this.projectIdToName.get(key)!;
    if (key.startsWith('[Task] ')) return key.replace(/^\[Task\] /, '');
    return key;
  }

  getEntryName(projectName: string | null | undefined): string {
    const name = projectName ?? '';
    return name.startsWith('[Task] ') ? name.replace(/^\[Task\] /, '') : name;
  }

  getEntryType(projectName: string | null | undefined): string {
    return (projectName ?? '').startsWith('[Task] ') ? 'CRM Task' : 'Project';
  }

  getDuration(startTime: Date | string, endTime: Date | string): string {
    const diff = new Date(endTime).getTime() - new Date(startTime).getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours < 1) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  get uniqueProjectNames(): string[] {
    return [...this.allKnownProjectNames].sort((a, b) =>
      this.resolveKeyName(a).localeCompare(this.resolveKeyName(b))
    );
  }

  get filteredProjectOptions(): string[] {
    const q = this.projectSearch.trim().toLowerCase();
    return q
      ? this.uniqueProjectNames.filter(n => this.resolveKeyName(n).toLowerCase().includes(q))
      : this.uniqueProjectNames;
  }
}
